import type { StateCreator } from 'zustand';
import { setLimitedObjectCacheEntry } from 'shared/utils/limitedCache';
import { fetchIssueChangelogMapped } from './fetchIssueChangelog';
import type { IssueChangelogEntry, IssueChangelogSlice } from './types';

type IssueChangelogSliceCreator<T> = StateCreator<T, [], [], IssueChangelogSlice>;

/** Full mapped changelogs are large — cap in-memory cache to avoid unbounded growth. */
export const MAX_ISSUE_CHANGELOG_CACHE_ENTRIES = 12;

function setChangelogCacheEntry(
  entries: Record<string, IssueChangelogEntry>,
  issueId: string,
  entry: IssueChangelogEntry
): Record<string, IssueChangelogEntry> {
  const nextEntries = { ...entries };
  setLimitedObjectCacheEntry(
    nextEntries,
    issueId,
    entry,
    MAX_ISSUE_CHANGELOG_CACHE_ENTRIES
  );
  return nextEntries;
}

export const createIssueChangelogSlice: IssueChangelogSliceCreator<any> = (
  set,
  get
) => {
  const inflightIssueChangelogRequests = new Map<string, Promise<void>>();

  return {
    issueChangelogEntries: {},

    requestIssueChangelog: async (issueId, options): Promise<void> => {
      const { force = false } = options ?? {};
      const existing = get().issueChangelogEntries[issueId];

      if (
        !force &&
        existing &&
        !existing.loading &&
        existing.error === null &&
        existing.fetched
      ) {
        return;
      }

      const inflight = inflightIssueChangelogRequests.get(issueId);
      if (inflight && !force) {
        return inflight;
      }

      let requestPromise!: Promise<void>;
      requestPromise = (async (): Promise<void> => {
        set((state: IssueChangelogSlice) => ({
          issueChangelogEntries: setChangelogCacheEntry(
            state.issueChangelogEntries,
            issueId,
            {
              mapped: existing?.mapped ?? [],
              loading: true,
              error: null,
              fetched: existing?.fetched ?? false,
            }
          ),
        }));

        try {
          const mapped = await fetchIssueChangelogMapped(issueId);
          if (inflightIssueChangelogRequests.get(issueId) !== requestPromise) {
            return;
          }
          set((state: IssueChangelogSlice) => ({
            issueChangelogEntries: setChangelogCacheEntry(
              state.issueChangelogEntries,
              issueId,
              {
                mapped,
                loading: false,
                error: null,
                fetched: true,
              }
            ),
          }));
        } catch (error) {
          if (inflightIssueChangelogRequests.get(issueId) !== requestPromise) {
            return;
          }
          set((state: IssueChangelogSlice) => ({
            issueChangelogEntries: setChangelogCacheEntry(
              state.issueChangelogEntries,
              issueId,
              {
                mapped: [],
                loading: false,
                error: error instanceof Error ? error.message : String(error),
                fetched: false,
              }
            ),
          }));
        }
      })().finally(() => {
        if (inflightIssueChangelogRequests.get(issueId) === requestPromise) {
          inflightIssueChangelogRequests.delete(issueId);
        }
      });

      inflightIssueChangelogRequests.set(issueId, requestPromise);
      return requestPromise;
    },

    invalidateIssueChangelog: (issueId: string): void => {
      set((state: IssueChangelogSlice) => {
        if (!state.issueChangelogEntries[issueId]) {
          return state;
        }
        const { [issueId]: _removed, ...rest } = state.issueChangelogEntries;
        return { issueChangelogEntries: rest };
      });
    },

    resetIssueChangelog: (): void => {
      inflightIssueChangelogRequests.clear();
      set({ issueChangelogEntries: {} });
    },
  };
};

export type { IssueChangelogSlice } from './types';
